import FilterChip from 'LuxKit/Chips/FilterChip'
import Heading from 'LuxKit/Typography/Heading'
import { rem } from 'polished'
import { useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import HorizontalContainerWithFadedOverflow from 'components/Common/HorizontalContainerWithFadedOverflow'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TrendingDestinationsCarousel from './TrendingDestinationsCarousel'
import TextButton from 'LuxKit/Button/TextButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import { FormattedMessage } from 'react-intl'
import LocalDestinationOfferCarousel from 'home/components/OfferCarousels/LocalDestinationOfferCarousel'
import HomepageOfferCarousel from 'home/components/OfferCarousels/dedup/HomepageOfferCarousel'
import { useTrendingDestinationsSectionData } from 'home/hooks/useTrendingDestinationsSectionData'
import { DEFAULT_TRENDING_DESTINATIONS_FILTER } from './trendingDestinationsSectionUtils'
import type { OfferListEventHandler } from 'components/OfferList/OfferListEventsContext'

const SectionContainer = styled(VerticalSpacer)`
  padding-top: ${rem(40)};
  padding-bottom: ${rem(40)};
`

export const DEFAULT_FILTER = DEFAULT_TRENDING_DESTINATIONS_FILTER

interface Props {
  title?: React.ReactNode
  titleVariant?: React.ComponentProps<typeof Heading>['variant']
  defaultFilter?: App.DestinationSelectedFilters
  filterOrder?: Record<App.DestinationSelectedFilters, number>
  hideFilterOptions?: boolean
  hideMapLink?: boolean
  onLocalDestinationOfferListEvent?: OfferListEventHandler
  excludedLocalDestinationOfferIds?: Array<string> | null
}

function TrendingDestinations(props: Props) {
  const {
    title,
    defaultFilter = DEFAULT_FILTER,
    hideFilterOptions,
    hideMapLink,
    titleVariant = 'heading2',
    onLocalDestinationOfferListEvent,
    excludedLocalDestinationOfferIds,
    filterOrder,
  } = props

  const {
    selectedFilter,
    setSelectedFilter,
    sortedFilters,
    currentRegionCode,
    fetchingDestinations,
    homepageTrendingDestinations,
    showLocalDestinationOfferCarousel,
    searchMapLink,
    intl,
  } = useTrendingDestinationsSectionData({
    defaultFilter,
    filterOrder,
  })

  const localDestinationOfferCarousel = useMemo(() => {
    if (
      !showLocalDestinationOfferCarousel
      || !onLocalDestinationOfferListEvent
      || excludedLocalDestinationOfferIds === null
    ) {
      return null
    }

    return (
      <HomepageOfferCarousel id="localDestinationOfferCarousel" mode="respect">
        <LocalDestinationOfferCarousel
          regionCode={currentRegionCode}
          onListEvent={onLocalDestinationOfferListEvent}
          excludedIds={excludedLocalDestinationOfferIds}
        />
      </HomepageOfferCarousel>
    )
  }, [
    showLocalDestinationOfferCarousel,
    currentRegionCode,
    onLocalDestinationOfferListEvent,
    excludedLocalDestinationOfferIds,
  ])

  return (
    <SectionContainer gap={32}>
      <LayoutContainer>
        <VerticalSpacer gap={12}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
            <Heading variant={titleVariant}>
              {title ?? (
                <FormattedMessage
                  id="xAzTT7yDe0Lv8y8amGCL"
                  defaultMessage="Immerse yourself in trending <i>destinations</i>"
                  description="Title for trending destinations section with italic 'destinations'"
                  values={{
                    i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
                  }}
                />
              )}
            </Heading>
            {!hideMapLink && <CSSBreakpoint min="desktop">
              <TextButton kind="tertiary" size="medium" startIcon={<LineMapIcon />} to={searchMapLink} target="_blank">
                {intl.formatMessage({
                  id: 'NHUdUWVryxXRFL7KXsgf',
                  defaultMessage: 'View map',
                  description: 'Button text to view destinations on map on the map or location view',
                })}
              </TextButton>
            </CSSBreakpoint>}
          </Group>
          {!hideFilterOptions && <HorizontalContainerWithFadedOverflow gap={4}>
            {sortedFilters.map((filter) => <FilterChip
              selected={filter.id === selectedFilter}
              onClick={() => setSelectedFilter(filter.id)}
              key={filter.id}
              size="medium"
            >
              {filter.title}
            </FilterChip>)}
          </HorizontalContainerWithFadedOverflow>}
        </VerticalSpacer>
      </LayoutContainer>
      <TrendingDestinationsCarousel
        fetchingDestinations={fetchingDestinations}
        selectedFilter={selectedFilter}
        trendingDestinations={homepageTrendingDestinations}
      />
      {localDestinationOfferCarousel}
    </SectionContainer>
  )
}

export default TrendingDestinations
